import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Diamond, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const SolitaireProcurement = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [procurements, setProcurements] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Solitaire options (same as order form)
  const diamondShapes = ['Round', 'Princess', 'Cushion', 'Oval', 'Emerald', 'Pear', 'Marquise', 'Radiant', 'Asscher', 'Heart', 'Sq. Emerald', 'Trilliant', 'Old European'];
  const colorTypes = ['White', 'Fancy'];
  const colorGrades = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'];
  const clarityGrades = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'I1', 'I2'];
  const cutGrades = ['Ideal', 'Excellent', 'Very Good', 'Good', 'Fair'];
  const polishGrades = ['Excellent', 'Very Good', 'Good', 'Fair'];
  const symmetryGrades = ['Ideal', 'Excellent', 'Very Good', 'Good', 'Fair'];
  const fluorescenceGrades = ['None', 'Very Slight', 'Faint', 'Slight', 'Medium', 'Strong'];
  const labOptions = ['GIA', 'IGI', 'HRD', 'AGS', 'GIA DOR', 'None'];

  // Form state
  const [form, setForm] = useState({
    title: '',
    quantity: '1',
    targetDate: '',
    budget: '',
    notes: '',
  });

  const [currentSolitaire, setCurrentSolitaire] = useState({
    shape: '',
    sizeFrom: '',
    sizeTo: '',
    color: '',
    colorGrades: [],
    clarities: [],
    cut: '',
    polish: '',
    symmetry: '',
    fluorescences: [],
    labs: [],
    notes: '',
    estimateAmount: '',
    source: 'to_source',
  });

  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [procurementsRes, brokersRes] = await Promise.all([
        api.get('/solitaire-procurements'),
        api.get('/users/by-role/broker').catch(() => ({ data: [] })),
      ]);
      setProcurements(procurementsRes.data);
      setBrokers(brokersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (field, value) => {
    const currentValues = currentSolitaire[field] || [];
    if (currentValues.includes(value)) {
      setCurrentSolitaire({
        ...currentSolitaire,
        [field]: currentValues.filter(v => v !== value)
      });
    } else {
      setCurrentSolitaire({
        ...currentSolitaire,
        [field]: [...currentValues, value]
      });
    }
  };

  const handleCreate = async () => {
    if (!form.title || !currentSolitaire.shape) {
      toast.error('Please enter title and select shape');
      return;
    }

    try {
      const procurementData = {
        title: form.title,
        solitaire_specs: {
          shape: currentSolitaire.shape,
          size_from: currentSolitaire.sizeFrom ? parseFloat(currentSolitaire.sizeFrom) : null,
          size_to: currentSolitaire.sizeTo ? parseFloat(currentSolitaire.sizeTo) : null,
          color: currentSolitaire.color || null,
          color_grades: currentSolitaire.colorGrades || [],
          clarities: currentSolitaire.clarities || [],
          cut: currentSolitaire.cut || null,
          polish: currentSolitaire.polish || null,
          symmetry: currentSolitaire.symmetry || null,
          fluorescences: currentSolitaire.fluorescences || [],
          labs: currentSolitaire.labs || [],
          notes: currentSolitaire.notes || null,
          estimate_amount: currentSolitaire.estimateAmount ? parseFloat(currentSolitaire.estimateAmount) : null,
          source: currentSolitaire.source,
        },
        quantity: parseInt(form.quantity) || 1,
        target_date: form.targetDate || null,
        budget: form.budget ? parseFloat(form.budget) : null,
        notes: form.notes || null,
      };

      await api.post('/solitaire-procurements', procurementData);
      toast.success('Procurement request created!');
      setIsCreateDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating procurement:', error);
      toast.error(error.response?.data?.detail || 'Failed to create request');
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      quantity: '1',
      targetDate: '',
      budget: '',
      notes: '',
    });
    setCurrentSolitaire({
      shape: '',
      sizeFrom: '',
      sizeTo: '',
      color: '',
      colorGrades: [],
      clarities: [],
      cut: '',
      polish: '',
      symmetry: '',
      fluorescences: [],
      labs: [],
      notes: '',
      estimateAmount: '',
      source: 'to_source',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      requested: 'bg-gray-100 text-gray-700',
      sourcing: 'bg-blue-100 text-blue-700',
      quoted: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      purchased: 'bg-purple-100 text-purple-700',
      delivered: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (!currentUser) {
    return <div>Please log in</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#075E54] text-white p-3 sticky top-0 z-10 shadow-md">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-[#128C7E] flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-semibold flex items-center gap-2">
                <Diamond className="h-5 w-5 flex-shrink-0" />
                <span className="truncate">Solitaire Procurement</span>
              </h1>
              <p className="text-xs text-emerald-100">Direct procurement requests</p>
            </div>
          </div>
          {(currentUser?.role === 'admin' || currentUser?.role === 'salesman') && (
            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              className="bg-[#25D366] hover:bg-[#20BA5A] text-sm h-9 px-3 flex-shrink-0"
            >
              <Plus className="h-4 w-4 mr-1" />
              New Request
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading procurements...</p>
          </div>
        ) : procurements.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <Diamond className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No procurement requests yet</p>
            {(currentUser?.role === 'admin' || currentUser?.role === 'salesman') && (
              <Button
                onClick={() => setIsCreateDialogOpen(true)}
                className="mt-4 bg-[#25D366] hover:bg-[#20BA5A]"
              >
                Create First Request
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {procurements.map((proc) => (
              <div
                key={proc.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-4 cursor-pointer"
                onClick={() => navigate(`/solitaire-procurement/${proc.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{proc.procurement_number}</h3>
                    <p className="text-sm text-gray-600">{proc.title}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(proc.status)}`}>
                    {proc.status}
                  </span>
                </div>

                {/* Solitaire Specs */}
                <div className="space-y-1 mb-3 text-sm">
                  <p className="text-gray-700">
                    <span className="font-medium">Shape:</span> {proc.solitaire_specs?.shape || 'N/A'}
                  </p>
                  {proc.solitaire_specs?.size_from && (
                    <p className="text-gray-700">
                      <span className="font-medium">Size:</span> {proc.solitaire_specs.size_from}
                      {proc.solitaire_specs.size_to && ` - ${proc.solitaire_specs.size_to}`} ct
                    </p>
                  )}
                  {proc.solitaire_specs?.color_grades?.length > 0 && (
                    <p className="text-gray-700">
                      <span className="font-medium">Color:</span> {proc.solitaire_specs.color_grades.join(', ')}
                    </p>
                  )}
                  {proc.solitaire_specs?.clarities?.length > 0 && (
                    <p className="text-gray-700">
                      <span className="font-medium">Clarity:</span> {proc.solitaire_specs.clarities.join(', ')}
                    </p>
                  )}
                  <p className="text-gray-700">
                    <span className="font-medium">Qty:</span> {proc.quantity}
                  </p>
                </div>

                {/* Broker & Budget */}
                <div className="border-t pt-2 text-sm">
                  {proc.assigned_broker_names && proc.assigned_broker_names.length > 0 && (
                    <p className="text-blue-600">
                      👤 {proc.assigned_broker_names.join(', ')}
                    </p>
                  )}
                  {proc.budget && (
                    <p className="text-green-600 font-medium">
                      Budget: ₹{proc.budget.toLocaleString('en-IN')}
                    </p>
                  )}
                  {proc.quoted_amount && (
                    <p className="text-purple-600 font-medium">
                      Quoted: ₹{proc.quoted_amount.toLocaleString('en-IN')}
                    </p>
                  )}
                </div>

                {/* Date */}
                <div className="text-xs text-gray-500 mt-2">
                  {new Date(proc.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog with Complete Solitaire Form */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Solitaire Procurement Request</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label>Title/Description *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Round brilliant for engagement ring order"
                  className="mt-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    className="mt-2"
                    min="1"
                  />
                </div>
                <div>
                  <Label>Target Date</Label>
                  <Input
                    type="date"
                    value={form.targetDate}
                    onChange={(e) => setForm({ ...form, targetDate: e.target.value })}
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label>Budget (₹)</Label>
                <Input
                  type="number"
                  value={form.budget}
                  onChange={(e) => setForm({ ...form, budget: e.target.value })}
                  placeholder="Maximum budget"
                  className="mt-2"
                />
              </div>
            </div>

            {/* Solitaire Specifications - Exact Copy from Order Form */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                <Diamond className="w-4 h-4 mr-2" />
                Solitaire Specifications
              </h3>

              {/* Shape Selection */}
              <div className="space-y-2 mb-4">
                <Label>Shape *</Label>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {diamondShapes.map((shape) => (
                    <button
                      key={shape}
                      type="button"
                      onClick={() => setCurrentSolitaire({ ...currentSolitaire, shape })}
                      className={`p-2 text-xs border rounded-lg transition-colors ${
                        currentSolitaire.shape === shape
                          ? 'bg-emerald-500 text-white border-emerald-500'
                          : 'bg-white hover:bg-gray-50 border-gray-200'
                      }`}
                    >
                      {shape}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size Range */}
              <div className="space-y-2 mb-4">
                <Label>Size (Carat)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="From (e.g., 0.30)"
                    value={currentSolitaire.sizeFrom}
                    onChange={(e) => setCurrentSolitaire({ ...currentSolitaire, sizeFrom: e.target.value })}
                  />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="To (e.g., 0.50)"
                    value={currentSolitaire.sizeTo}
                    onChange={(e) => setCurrentSolitaire({ ...currentSolitaire, sizeTo: e.target.value })}
                  />
                </div>
              </div>

              {/* Color */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Color Type</Label>
                  <Select
                    value={currentSolitaire.color}
                    onValueChange={(value) => setCurrentSolitaire({ ...currentSolitaire, color: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {colorTypes.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Color Grade <span className="text-gray-400 text-xs">(multi-select)</span></Label>
                  <div className="flex flex-wrap gap-1">
                    {colorGrades.map((grade) => (
                      <button
                        key={grade}
                        type="button"
                        onClick={() => toggleSelection('colorGrades', grade)}
                        className={`px-2 py-1 text-xs border rounded transition-colors ${
                          currentSolitaire.colorGrades?.includes(grade)
                            ? 'bg-emerald-500 text-white border-emerald-500'
                            : 'bg-white hover:bg-gray-50'
                        }`}
                      >
                        {grade}
                      </button>
                    ))}
                  </div>
                  {currentSolitaire.colorGrades?.length > 0 && (
                    <p className="text-xs text-emerald-600 font-medium">
                      Selected: {currentSolitaire.colorGrades.join(', ')}
                    </p>
                  )}
                </div>
              </div>

              {/* Clarity */}
              <div className="space-y-2 mb-4">
                <Label>Clarity <span className="text-gray-400 text-xs">(multi-select)</span></Label>
                <div className="flex flex-wrap gap-1">
                  {clarityGrades.map((grade) => (
                    <button
                      key={grade}
                      type="button"
                      onClick={() => toggleSelection('clarities', grade)}
                      className={`px-2 py-1 text-xs border rounded transition-colors ${
                        currentSolitaire.clarities?.includes(grade)
                          ? 'bg-emerald-500 text-white border-emerald-500'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      {grade}
                    </button>
                  ))}
                </div>
                {currentSolitaire.clarities?.length > 0 && (
                  <p className="text-xs text-emerald-600 font-medium">
                    Selected: {currentSolitaire.clarities.join(', ')}
                  </p>
                )}
              </div>

              {/* Cut, Polish, Symmetry */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Cut</Label>
                  <Select
                    value={currentSolitaire.cut}
                    onValueChange={(value) => setCurrentSolitaire({ ...currentSolitaire, cut: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {cutGrades.map((g) => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Polish</Label>
                  <Select
                    value={currentSolitaire.polish}
                    onValueChange={(value) => setCurrentSolitaire({ ...currentSolitaire, polish: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {polishGrades.map((g) => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Symmetry</Label>
                  <Select
                    value={currentSolitaire.symmetry}
                    onValueChange={(value) => setCurrentSolitaire({ ...currentSolitaire, symmetry: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {symmetryGrades.map((g) => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Fluorescence */}
              <div className="space-y-2 mb-4">
                <Label>Fluorescence <span className="text-gray-400 text-xs">(multi-select)</span></Label>
                <div className="flex flex-wrap gap-1">
                  {fluorescenceGrades.map((grade) => (
                    <button
                      key={grade}
                      type="button"
                      onClick={() => toggleSelection('fluorescences', grade)}
                      className={`px-2 py-1 text-xs border rounded transition-colors ${
                        currentSolitaire.fluorescences?.includes(grade)
                          ? 'bg-emerald-500 text-white border-emerald-500'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      {grade}
                    </button>
                  ))}
                </div>
                {currentSolitaire.fluorescences?.length > 0 && (
                  <p className="text-xs text-emerald-600 font-medium">
                    Selected: {currentSolitaire.fluorescences.join(', ')}
                  </p>
                )}
              </div>

              {/* Grading Lab */}
              <div className="space-y-2 mb-4">
                <Label>Grading Lab / Certificate <span className="text-gray-400 text-xs">(multi-select)</span></Label>
                <div className="flex flex-wrap gap-1">
                  {labOptions.map((lab) => (
                    <button
                      key={lab}
                      type="button"
                      onClick={() => toggleSelection('labs', lab)}
                      className={`px-2 py-1 text-xs border rounded transition-colors ${
                        currentSolitaire.labs?.includes(lab)
                          ? 'bg-emerald-500 text-white border-emerald-500'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      {lab}
                    </button>
                  ))}
                </div>
                {currentSolitaire.labs?.length > 0 && (
                  <p className="text-xs text-emerald-600 font-medium">
                    Selected: {currentSolitaire.labs.join(', ')}
                  </p>
                )}
              </div>

              {/* Estimate Amount & Source */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estimate Amount (₹)</Label>
                  <Input
                    type="number"
                    value={currentSolitaire.estimateAmount}
                    onChange={(e) => setCurrentSolitaire({ ...currentSolitaire, estimateAmount: e.target.value })}
                    placeholder="Expected price"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select
                    value={currentSolitaire.source}
                    onValueChange={(value) => setCurrentSolitaire({ ...currentSolitaire, source: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="to_source">To Source</SelectItem>
                      <SelectItem value="in_stock">In Stock</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Solitaire Notes */}
              <div>
                <Label>Solitaire Notes</Label>
                <textarea
                  value={currentSolitaire.notes}
                  onChange={(e) => setCurrentSolitaire({ ...currentSolitaire, notes: e.target.value })}
                  rows={2}
                  className="w-full mt-2 px-3 py-2 border rounded-md text-sm"
                  placeholder="Special requirements for this solitaire..."
                />
              </div>
            </div>

            {/* Additional Notes */}
            <div className="border-t pt-4">
              <Label>Additional Notes</Label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="w-full mt-2 px-3 py-2 border rounded-md"
                placeholder="Any additional procurement requirements..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsCreateDialogOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} className="bg-[#25D366] hover:bg-[#20BA5A]">
              Create Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SolitaireProcurement;
